<template>
  <div
    ref="el"
    class="template-preview ql-bubble"
    :class="{
      'element-library-open': showElementLibrary,
    }"
  >
    <div
      v-if="showGuides"
      class="guides-container"
      data-test="graphics-guidesContainer"
    >
      <div
        ref="guideCorner"
        class="guideCorner"
        data-test="graphics-guideCorner"
      />
      <vue-guides
        ref="guidesHorizontal"
        :style="horizontalGuideStyle"
        type="horizontal"
        :display-drag-pos="true"
        :zoom="scaleFactor"
        :unit="guideUnit"
        background-color="#fefeff"
        text-color="#2bacca"
        line-color="#2bacca"
        class-name="tgb-guides"
        data-test="graphics-guidesHorizontal"
        @change-guides="onChangeHGuides"
      />
      <vue-guides
        ref="guidesVertical"
        :style="verticalGuideStyle"
        type="vertical"
        :display-drag-pos="true"
        :zoom="scaleFactor"
        :unit="guideUnit"
        background-color="#fefeff"
        text-color="#2bacca"
        line-color="#2bacca"
        class-name="tgb-guides"
        data-test="graphics-guidesVertical"
        @change-guides="onChangeVGuides"
      />
    </div>
    <div
      ref="previewContent"
      class="preview-content"
      :style="templatePreviewStyle"
      data-test="graphics-previewContent"
      @mousedown="startElementDraw"
      @mouseup="previewContentMouseUp"
    >
      <div
        ref="dragSelector"
        class="drag-selector"
        data-test="graphics-canvasDragSelector"
      />
      <pan-zoom
        ref="panZoom"
        :options="{
          initialZoom,
          filterKey: () => true,
          maxZoom: maxZoom / 100,
          minZoom: minZoom / 100,
          beforeWheel,
          beforeMouseDown,
          autoCenter: true,
          smoothScroll: false,
          zoomDoubleClickSpeed: 1,
        }"
        data-test="graphics-panZoomContainer"
        @init="onZoomInit"
        @zoom="onZoom"
        @pan="onPan"
        @panstart="onPanStart"
      >
        <div
          ref="previewContainer"
          class="preview-container"
          data-test="graphics-previewContainer"
          :class="fullscreen ? 'preview-container--fullscreen' : ''"
          :style="previewContainerStyle"
        >
          <div
            ref="canvas"
            class="canvas"
            data-test="graphics-graphicCanvas"
            :class="getSnippetClasses"
          >
            <element-renderer
              v-for="elementId in rootElementsReversed"
              v-show="!getElementById(elementId)?.layerHiddenInGraphics"
              :id="elementId"
              :key="elementId"
              :type="getElementById(elementId).type"
              :toggle="animationToggle"
              parent="root"
              :data-test="`graphics-element-${
                getElementById(elementId).type
              }-${elementId}`"
              @select="selectElement"
            />

            <resizer
              v-show="
                selectedElement &&
                  !selectedElement.locked &&
                  !selectedElement.disableResize
              "
              data-test="graphics-elementResizer"
              :element="selectedElement"
              :class="resizerClassName"
              :scale="scaleFactor"
            />

            <div
              v-if="showSafeZone"
              class="guides"
              data-test="graphics-safeZone"
              :style="{
                width: getSafeArea.width + '%',
                height: getSafeArea.height + '%',
              }"
            />
          </div>
        </div>
      </pan-zoom>
    </div>
    <!-- todo: remove left top or hide/show via LD flags or z-index some shit -->
    <div
      ref="overlayMask"
      class="overlay-mask"
      data-test="graphics-canvasOverlayMask"
    >
      <!-- useing two one pixel png with clipping mask to make a less janky off-canvas mask area -->
      <img
        ref="overlayMaskBase"
        class="overlay-mask--base"
        src="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAAAXNSR0IArs4c6QAAAA1JREFUGFdj+P///38ACfsD/QVDRcoAAAAASUVORK5CYII="
      />
    </div>
  </div>
</template>

<script>
import { cloneDeep, sortBy, find } from 'lodash-es';

import VueGuides from 'vue3-guides';
// docs for vue guides:
// https://github.com/daybrush/guides/tree/master/packages/vue-guides
// https://daybrush.com/guides/release/latest/doc/Guides.html#scroll
// https://github.com/daybrush/ruler
// https://daybrush.com/ruler/release/latest/doc/Ruler.html#.RulerProps-segment
import { mapState, mapGetters, mapActions } from 'vuex';
import ConduitEventsService from '../services/conduit';
import EventBus from '../utils/event-bus';
import defaults from '../utils/defaults';
import { elementTypesWithoutPosition } from '../utils/constants';

import ElementRenderer from './Elements/ElementRenderer';
import Resizer from './Resizer.vue';
import PanZoom from './PanZoom.vue';

/**
 * Returns true if elements a and b intersect each other
 * @param {Object} a - Drag Selector
 * @param {Object} b - Canvas Element
 * @returns {Boolean}
 */
function intersects(a, b) {
  const rectA = a.getBoundingClientRect();
  const rectB = b.getBoundingClientRect();

  // Easiest way to check for overlaps, is to check if the elements DON'T overlap and invert
  return !(
    rectA.top > rectB.bottom
    || rectA.bottom < rectB.top
    || rectA.left > rectB.right
    || rectA.right < rectB.left
  );
}

export default {
  components: {
    ElementRenderer,
    Resizer,
    VueGuides,
    PanZoom,
  },
  provide() {
    return { isRenderMode: false };
  },
  props: {
    showElementLibrary: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      fullscreen: false,
      altKey: false,
      resizeTimeout: null,
      didDrag: false,
      onLoadGuides: null,
      scrollX: 0,
      scrollY: 0,
      baseMargin: 30,
      guideWidth: 30,
      footerHeight: 40,
      panZoomInstance: null,
      initialH: 0,
      initialW: 0,
      elementDrawStartX: 0,
      elementDrawStartY: 0,
      elementDrawEndX: 0,
      elementDrawEndY: 0,
      elementDrawDrag: true,
      elementIsDrawing: false,
      restoreZoomAfterResize: false,
    };
  },
  computed: {
    ...mapState({
      textEditing: (state) => state.templates.textEditing,
      animationToggle: (state) => state.templates.animationToggle,
      selectedElements: (state) => state.templates.selectedElements,
      snippets: (state) => state.user.snippets,
    }),

    ...mapGetters('graphicsTemplate', [
      'getCanvas',
      'getTemplateSnippets',
      'getSafeArea',
      'getGuides',
      'getElementGroupMap',
      'getElementById',
      'getAllParents',
      'getAbsolutePosition',
      'getElementParentGroupId',
      'getTemplateSocialData',
      'getQueuedElement',
    ]),

    ...mapGetters('panAndZoom', {
      scaleFactor: 'getScaleFactor',
      panModeState: 'getPanMode',
      minZoom: 'getMinZoom',
      maxZoom: 'getMaxZoom',
      scaleFactorModeState: 'getScaleFactorMode',
      scaleFactorSelectedState: 'getScaleFactorSelected',
      scaleFactorFitState: 'getScaleFactorFit',
    }),

    queuedElement: {
      get() {
        return this.getQueuedElement;
      },
      set(v) {
        this.setPanMode(v);
      },
    },
    initialZoom() {
      return this.scaleFactorFit;
    },

    panMode: {
      get() {
        return this.panModeState;
      },
      set(v) {
        this.setPanMode(v);
      },
    },

    scaleFactorMode: {
      get() {
        return this.scaleFactorModeState;
      },
      set(v) {
        this.setScaleFactorMode(v);
      },
    },

    scaleFactorSelected: {
      get() {
        return this.scaleFactorSelectedState;
      },
      set(v) {
        this.setScaleFactorSelected(v);
      },
    },

    scaleFactorFit: {
      get() {
        return this.scaleFactorFitState;
      },
      set(v) {
        this.setScaleFactorFit(v);
      },
    },

    rootElementsReversed() {
      return [...this.getElementGroupMap.root].reverse();
    },

    showSafeZone() {
      return this.getSafeArea.show;
    },

    showGuides() {
      return this.getGuides.show;
    },

    selectedElement() {
      if (this.selectedElements.length !== 1) {
        return null;
      }

      const [selectedElement] = this.selectedElements;
      return this.getElementById(selectedElement);
    },
    guideUnit() {
      if (this.scaleFactor < 0.58) {
        return 100;
      }
      return 50;
    },

    // computed Styles
    horizontalGuideStyle() {
      return {
        height: `${this.guideWidth}px`,
        left: `${this.guideWidth}px`,
      };
    },
    verticalGuideStyle() {
      return {
        width: `${this.guideWidth}px`,
      };
    },
    templatePreviewStyle() {
      const { baseMargin: margin, guideWidth, footerHeight } = this;
      if (this.showGuides) {
        return {
          height: `calc(100% - ${margin * 2 + guideWidth + footerHeight}px)`,
          width: `calc(100% - ${margin * 2 + guideWidth}px)`,
          margin: `${margin + guideWidth}px ${margin}px ${margin}px ${
            margin + guideWidth
          }px`,
        };
      }
      return {
        height: `calc(100% - ${margin * 2 + footerHeight}px)`,
        width: `calc(100% - ${margin * 2}px)`,
        margin: `${margin}px`,
      };
    },
    previewContainerStyle() {
      const { width, height } = this.getCanvas;

      return {
        width: `${width}px`,
        height: `${height}px`,
      };
    },

    // computed Classes
    resizerClassName() {
      const element = this.selectedElement;
      if (!element) {
        return 'resizer';
      }

      const resizable = element.type !== 'group';
      return `resizer ${resizable ? '' : 'group-resizer'}`;
    },
    getSnippetClasses() {
      let classname = '';

      if (this.getTemplateSnippets) {
        this.getTemplateSnippets.forEach((s) => {
          classname += ` snp-${s}`;
        });
      }

      return classname;
    },
    panzoomElement() {
      return this.$refs.previewContainer;
    },
  },
  watch: {
    getTemplateSnippets() {
      try {
        this.registerCustomCSS();
      } catch (err) {
        console.error(err);
      }
    },

    scrollX() {
      this.$refs.guidesHorizontal?.scroll(this.scrollX);
      this.$refs.guidesVertical?.scrollGuides(this.scrollX);
      this.$refs.guidesHorizontal?.resize();
    },

    scrollY() {
      this.$refs.guidesVertical?.scroll(this.scrollY);
      this.$refs.guidesHorizontal?.scrollGuides(this.scrollY);
      this.$refs.guidesHorizontal?.resize();
    },

    getCanvas: {
      handler() {
        this.$nextTick(() => {
          this.resizeTheCanvasMask();
        });
      },
      deep: true,
    },

    scaleFactorSelected() {
      this.setFactor();
    },

    scaleFactorFit(v) {
      this.scaleFactorSelected = v * 100;
    },

    scaleFactorMode() {
      this.setFactor();
    },

    panMode(v) {
      if (v) {
        this.$refs.panZoom.$el.style.cursor = 'grab';
      } else {
        this.$refs.panZoom.$el.style.cursor = this.queuedElement
          ? 'crosshair'
          : 'auto';
      }
    },

    queuedElement(v) {
      if (v) {
        this.$refs.panZoom.$el.style.cursor = 'crosshair';
        // Add click listener when element is queued
        setTimeout(() => {
          document.addEventListener('click', this.handleClickOutsideCanvas);
        });
      } else {
        // Remove click listener when no queued element
        document.removeEventListener('click', this.handleClickOutsideCanvas);
      }
    },

    showElementLibrary() {
      // zoom has been adjusted, restore it after resizing
      if (this.scaleFactorSelected / 100 !== this.scaleFactorFit) {
        this.restoreZoomAfterResize = true;
      }
    },
  },
  mounted() {
    this.onLoadGuides = this.getGuides; // in case we need to reset

    this.onRulersLoadGuides = () => {
      this.$nextTick(() => {
        this.resetTheGuides();
        this.resizeTheCanvasMask();
      });
    };
    EventBus.on('rulers-load-guides', this.onRulersLoadGuides);

    this.resetTheGuides();

    document.addEventListener('keydown', this.onKeyDown);
    document.addEventListener('keyup', this.onKeyUp);
    window.addEventListener('scroll', this.onScroll);

    this.$refs.panZoom.$el.addEventListener('pointerdown', this.onPointerDown);

    // Fires when all fonts have been loaded by the browser
    document.fonts.onloadingdone = () => EventBus.emit('fonts-loaded');

    EventBus.on('align', this.alignElements);
    EventBus.on('distribute', this.distributeElements);
    EventBus.on('keyup-arrow', this.nudgeElements);

    this.calcScaleFactorFit();

    // Set initial scale factor and pan mode
    const newlyRendered = true;
    this.setFactor(newlyRendered);

    try {
      this.registerCustomCSS();
    } catch (err) {
      console.error(err);
    }

    this.dragSelector = this.$el.querySelector('.drag-selector');

    this.$el.addEventListener('mousedown', this.onMouseDown);

    this.resizeObserver = new ResizeObserver(() => {
      this.resize();
    });

    if (this.$refs.el) {
      this.resizeObserver.observe(this.$refs.el);
    }
  },
  beforeUnmount() {
    EventBus.off('align', this.alignElements);
    EventBus.off('distribute', this.distributeElements);
    EventBus.off('rulers-load-guides', this.onRulersLoadGuides);
    EventBus.off('keyup-arrow', this.nudgeElements);
    document.removeEventListener('keydown', this.onKeyDown);
    document.removeEventListener('keyup', this.onKeyUp);
    // Click listener is managed by queuedElement watcher
    this.$refs.panZoom.$el.removeEventListener(
      'pointerdown',
      this.onPointerDown,
    );
    this.$el.removeEventListener('mousedown', this.onMouseDown);
    window.removeEventListener('scroll', this.onScroll);

    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
  },
  methods: {
    ...mapActions('graphicsTemplate', [
      'setGuideHorizontal',
      'setGuideVertical',
      'onMoveOrResize',
      'incrementElementPosition',
      'decrementElementPosition',
      'addNewElement',
      'setQueuedElement',
      'duplicateElement',
      'setOneElementValueByProp',
    ]),

    ...mapActions('panAndZoom', [
      'setScaleFactor',
      'setPanMode',
      'setScaleFactorMode',
      'setScaleFactorSelected',
      'setScaleFactorFit',
    ]),

    handleClickOutsideCanvas(e) {
      // Check if click is outside the canvas area
      const canvasEl = this.$refs.canvas;
      if (!canvasEl) {
        return;
      }

      // Ignore clicks within modals (e.g., social-display/poll-display selection modals)
      if (e.target.closest('.modal-mask')) {
        return;
      }

      // Check if the click target is within the canvas or its children
      if (!canvasEl.contains(e.target)) {
        // Clicked outside canvas - clear the queued element
        this.setQueuedElement({ value: null });
        if (this.$refs.panZoom?.$el) {
          this.$refs.panZoom.$el.style.cursor = 'auto';
        }
      }
    },

    startElementDraw(e) {
      if (!this.getQueuedElement && e.target.closest('.element')) return;
      this.elementIsDrawing = true;
      this.elementDrawStartX = e.clientX;
      this.elementDrawStartY = e.clientY;
    },

    setFactor(newlyRendered = false) {
      const parent = this.panzoomElement.parentElement;

      // Calculate center
      let centerX = parent.offsetWidth / 2;
      let centerY = parent.offsetHeight / 2;

      const factor = this.scaleFactorSelected / 100;
      if (this.scaleFactorMode === 1 && !newlyRendered) {
        this.panZoomInstance.smoothZoomAbs(centerX, centerY, factor);
      } else {
        this.centerContent();
        this.$nextTick(() => {
          centerX = parent.offsetWidth / 2;
          centerY = parent.offsetHeight / 2;
          this.panZoomInstance.smoothZoomAbs(centerX, centerY, factor);
        });
      }
    },

    calcScaleFactorFit() {
      let currentMarginTotal = this.baseMargin * 2;
      if (this.showGuides) {
        currentMarginTotal += this.guideWidth;
      }

      const fullContentArea = this.$refs.el;
      if (!fullContentArea) return;
      const previewRatio = this.getCanvas.width / this.getCanvas.height;
      const contentRatio = (fullContentArea.clientWidth - currentMarginTotal)
        / (fullContentArea.clientHeight - currentMarginTotal);

      // Default scale using width
      let difference = (fullContentArea.clientWidth - currentMarginTotal)
        / this.getCanvas.width;

      // Scale by height if ratio is < than ratio of preview area
      if (previewRatio < contentRatio) {
        difference = (fullContentArea.clientHeight
            - this.footerHeight
            - currentMarginTotal)
          / this.getCanvas.height;
      }

      this.scaleFactorFit = Number(difference.toFixed(2));
    },

    centerContent(smooth) {
      this.$nextTick(() => {
        const parent = this.panzoomElement.parentElement;
        // Calculate center
        const centerX = parent.offsetWidth / 2
          - (this.panzoomElement.offsetWidth * this.scaleFactor) / 2;
        const centerY = parent.offsetHeight / 2
          - (this.panzoomElement.offsetHeight * this.scaleFactor) / 2;
        if (smooth) {
          this.panZoomInstance.smoothMoveTo(centerX, centerY);
        }
        // Apply the pan
        this.panZoomInstance.moveTo(centerX, centerY);
      });
    },
    incrementPosition(positionType, stepSize = 1, id = this.element.id) {
      this.incrementElementPosition({
        value: { id, positionType, stepSize },
      });
    },
    decrementPosition(positionType, stepSize = 1, id = this.element.id) {
      this.decrementElementPosition({
        value: { id, positionType, stepSize },
      });
    },
    onChangeHGuides(e) {
      this.setGuideHorizontal({ value: e.guides });
    },
    onChangeVGuides(e) {
      this.setGuideVertical({ value: e.guides });
    },
    resetTheGuides() {
      this.$nextTick(() => {
        this.trackRulerAndGuideScrollPosition();
      });
      this.$refs.guidesHorizontal?.scroll(this.scrollX);
      this.$refs.guidesVertical?.scrollGuides(this.scrollX);
      this.$refs.guidesVertical?.scroll(this.scrollY);
      this.$refs.guidesHorizontal?.scrollGuides(this.scrollY);
      this.$refs.guidesHorizontal?.resize();
      this.$refs.guidesHorizontal?.loadGuides(this.getGuides.hGuides);
      this.$refs.guidesVertical?.resize();
      this.$refs.guidesVertical?.loadGuides(this.getGuides.vGuides);
    },

    onScroll() {
      this.resizeTheCanvasMask();
    },

    onPointerDown(e) {
      // if middle mouse, we are panning, so don't drag the element
      if (e.button === 1) {
        this.panMode = true;
        window.addEventListener('pointerup', this.onPointerUp);
      }
    },

    onPointerUp(e) {
      if (e.button === 1) {
        this.panMode = false;
        window.removeEventListener('pointerup', this.onPointerUp);
      }
    },

    onMouseDown(e) {
      if (this.textEditing) {
        return;
      }

      // A disabled child drag handle can let a group click bubble here. The
      // Selection starts on empty canvas; drawing a queued element can start
      // over an existing element and still needs the drag preview and endpoints.
      if (!this.queuedElement && e.target.closest('.element')) {
        return;
      }

      e.preventDefault();
      e.stopPropagation();

      this.dragSelector.classList.add('active');
      this.dragSelector.style.left = `${e.pageX}px`;
      this.dragSelector.style.top = `${e.pageY}px`;

      this.initialW = e.pageX;
      this.initialH = e.pageY;

      document.addEventListener('mouseup', this.unbindDragSelector);
      document.addEventListener('mousemove', this.openSelector);
    },

    onKeyDown(e) {
      if (
        this.textEditing
        || $(e.target).is('input')
        || $(e.target).is('textarea')
      ) {
        return;
      }
      /* NOTE: Enable for fullscreen toggle with CTRL-F
      if (e.ctrlKey && e.key === 'f') {
        e.preventDefault();
        this.fullscreen = !this.fullscreen;
      }
      */

      if (e.key === 'Escape' && this.fullscreen) {
        e.preventDefault();
        this.fullscreen = false;
      }

      if (e.key === 'Escape' && this.getQueuedElement) {
        e.preventDefault();
        this.setQueuedElement({ value: null });
        this.$refs.panZoom.$el.style.cursor = 'auto';
      }

      if (e.key === ' ') {
        this.panMode = true;
        this.$refs.panZoom.$el.style.cursor = 'grab';
      }

      if (e.key === 'Alt' && !this.altKey) {
        this.altKey = true;
      }

      if ((e.metaKey || e.ctrlKey) && e.key === '=') {
        e.preventDefault();
        this.scaleFactorMode = 1;
        if (this.scaleFactorSelected < this.maxZoom) {
          this.scaleFactorSelected += 5;
        }
      }

      if ((e.metaKey || e.ctrlKey) && e.key === '-') {
        e.preventDefault();
        this.scaleFactorMode = 1;
        if (this.scaleFactorSelected > this.minZoom) {
          this.scaleFactorSelected -= 5;
        }
      }

      if (e.key === 'h') {
        this.panMode = true;
        this.$refs.panZoom.$el.style.cursor = 'grab';
      }

      if (e.key === 'v') {
        this.panMode = false;
        this.$refs.panZoom.$el.style.cursor = 'auto';
      }

      if ((e.metaKey || e.ctrlKey) && e.key === '0') {
        e.preventDefault();
        this.scaleFactorMode = 0;
        this.scaleFactorSelected = this.scaleFactorFit * 100;
      }
    },

    onKeyUp(e) {
      if (e.key === 'Alt' && this.altKey) {
        this.altKey = false;
        this.$refs.panZoom.$el.style.cursor = 'grab';
      }

      if (e.key === ' ') {
        this.panMode = false;
        this.$refs.panZoom.$el.style.cursor = 'auto';
      }
    },

    blurActiveInput() {
      // Unfocus any focused text element.
      // I believe the resizer stops propagation which keeps inputs focused.
      const el = document.activeElement;
      if (el) {
        el.blur();
      }
    },

    previewContentMouseUp(e) {
      if (!this.elementIsDrawing) return; // skip if we are not drawing
      this.deselectElement(e);
      if (this.getQueuedElement) {
        this.addQueuedElement(e);
      }
    },

    async addQueuedElement(e) {
      const {
        scaleFactor,
        elementDrawEndX,
        elementDrawEndY,
        elementDrawStartX,
        elementDrawStartY,
        getQueuedElement,
        $refs,
      } = this;

      // Get the position of the div
      const rect = $refs.previewContainer.getBoundingClientRect();
      const { value } = getQueuedElement;
      const { type, settings } = value;
      const defaultStyles = defaults[type].styles;

      // tolerance for click add for folks who drop before they are fully done moving to position
      const tolerance = 1;

      const clickAdd = Math.abs(elementDrawStartX - e.clientX) <= tolerance
        || Math.abs(elementDrawStartY - e.clientY) <= tolerance;

      let styles = {};

      if (clickAdd) {
        // Special handling for social-display and poll-display: default to fullscreen
        if (type === 'social-display' || type === 'poll-display') {
          styles = {
            left: 0,
            top: 0,
            width: 1920,
            height: 1080,
          };
        } else {
          // Calculate the x, y coordinates inside the div
          const xInsideDiv = (e.clientX - rect.left) / scaleFactor;
          const yInsideDiv = (e.clientY - rect.top) / scaleFactor;

          styles = {
            left: Math.round(xInsideDiv - defaultStyles.width / 2),
            top: Math.round(yInsideDiv - defaultStyles.height / 2),
          };
        }
      } else {
        const xInsideDiv = Math.round(
          (Math.min(elementDrawStartX, elementDrawEndX) - rect.left) / scaleFactor,
        );
        const yInsideDiv = Math.round(
          (Math.min(elementDrawStartY, elementDrawEndY) - rect.top) / scaleFactor,
        );
        styles = {
          left: xInsideDiv,
          top: yInsideDiv,
          width: Math.abs(
            Math.round((elementDrawEndX - elementDrawStartX) / scaleFactor),
          ),
          height: Math.abs(
            Math.round((elementDrawEndY - elementDrawStartY) / scaleFactor),
          ),
        };
      }

      const newEl = await this.addNewElement({
        ...getQueuedElement,
        value: {
          ...value,
          settings: {
            ...settings,
            styles,
          },
        },
      });
      this.setQueuedElement({ value: null });
      $refs.panZoom.$el.style.cursor = 'auto';
      this.elementIsDrawing = false;
      this.$store.dispatch('selectElements', [newEl.id]);

      ConduitEventsService.track(
        'graphics.added.element',
        {
          template_id: this.templateId,
          owner: this.owner,
          type,
        },
        {
          ownerId: this.owner,
          userId: this.$store.state.user.user.id,
          isStaff: this.$store.state.user.isStaff,
        },
      );
    },

    deselectElement() {
      const { isResizing } = this.$store.state.templates;

      this.blurActiveInput();

      if (this.didDrag) {
        this.didDrag = false;
      } else if (!isResizing) {
        this.$store.dispatch('clearSelectedGroups');
        this.$store.dispatch('setTextEditing', false);
        this.$store.dispatch('selectElements', []);
      }
    },
    selectElement(event, id) {
      if (this.panMode) return;
      this.blurActiveInput();

      if (event.shiftKey) {
        this.$store.dispatch('updateSelectedElements', id);
      } else {
        this.$store.dispatch('selectElements', [id]);
      }
    },
    resize() {
      const self = this;
      clearTimeout(this.resizeTimeout);
      const storedZoom = this.scaleFactorSelected;

      this.resizeTimeout = setTimeout(() => {
        this.calcScaleFactorFit();

        self.$nextTick(() => {
          self.trackRulerAndGuideScrollPosition();
          self.resizeTheCanvasMask();
          if (this.restoreZoomAfterResize) {
            self.scaleFactorSelected = storedZoom;
            this.restoreZoomAfterResize = false;
          }
        });

        EventBus.emit('resize');
      }, 25);
    },
    trackRulerAndGuideScrollPosition() {
      if (this.$refs.guideCorner && this.$refs.previewContainer) {
        // as the canvas size adjust changes we need to track the coordinates of it's left corner
        // then calculate the ruler starting point to adjust for the UI elements around the ruler
        const previewContainerRect = this.$refs.previewContainer.getBoundingClientRect();
        const guideCornerRect = this.$refs.guideCorner.getBoundingClientRect();

        const OffsetCanvasY = previewContainerRect.top + window.scrollY;
        const OffsetCanvasX = previewContainerRect.left + window.scrollX;

        const OffsetGuideCornerY = guideCornerRect.bottom + window.scrollY;
        const OffsetGuideCornerX = guideCornerRect.right + window.scrollX;

        // then to get the right scroll value
        // (ie move the ruler 0 point to always align with the top left of the cavas)
        // we get the differance of the the two offsets and then factor by the scale of the canvas
        // and to move it the correct direction we need it as a neg number
        this.scrollY = -(OffsetCanvasY - OffsetGuideCornerY) / this.scaleFactor;
        this.scrollX = -(OffsetCanvasX - OffsetGuideCornerX) / this.scaleFactor;
      }
    },
    resizeTheCanvasMask() {
      // calculate the hole in the middle of the mask image
      if (this.$refs.previewContainer) {
        const previewContainerRect = this.$refs.previewContainer.getBoundingClientRect();

        const {
          top, left, bottom, right,
        } = previewContainerRect;

        // because we can't add js that depends on dom calcs in computed,
        // we inject it here with reg ol' js
        this.$refs.overlayMaskBase.style.clipPath = `polygon(0% 0%, 0% 100%, ${left}px 100%, ${left}px ${top}px, ${right}px ${top}px, ${right}px ${bottom}px, ${left}px ${bottom}px, ${left}px 100%, 100% 100%, 100% 0%)`;
      }
    },

    nudgeElements(event) {
      this.selectedElements.forEach((element) => {
        // Skip elements that don't have position settings
        if (
          elementTypesWithoutPosition.includes(
            this.getElementById(element).type,
          )
        ) return;
        switch (event.which) {
          case 37:
            this.decrementPosition('left', 1, element);
            break;
          case 38:
            this.decrementPosition('top', 1, element);
            break;
          case 39:
            this.incrementPosition('left', 1, element);
            break;
          case 40:
            this.incrementPosition('top', 1, element);
            break;
          default:
            break;
        }

        if (this.getElementParentGroupId(element) !== 'root') {
          this.onMoveOrResize({
            value: {
              groupId: this.getElementParentGroupId(element),
            },
          });
        }
      });
    },
    alignElements(align) {
      EventBus.emit('set-alignment', {
        align,
        positions: this.getSelectedElementPositions(),
      });

      for (let i = 0; i < this.selectedElements.length; i += 1) {
        const id = this.selectedElements[i];

        const parents = this.getAllParents(id);

        // Loop over parents of selected elements and recalculate bounds if item was in a group
        parents.forEach((el) => {
          this.onMoveOrResize({
            value: {
              groupId: el,
            },
          });
        });
      }

      EventBus.emit('sync-all');
    },
    distributeElements(distribute) {
      let totalInnerElementsSize = 0;
      const DISTRIBUTE_POSITION_LOOKUP = {
        horizontal: 'left',
        vertical: 'top',
      };
      const DISTRIBUTE_SIZE_LOOKUP = {
        horizontal: 'width',
        vertical: 'height',
      };
      const selectedElements = this.getSelectedElements();

      const elementsOrder = sortBy(
        selectedElements,
        (el) => el.styles[DISTRIBUTE_POSITION_LOOKUP[distribute]],
      );

      const innerSpace = elementsOrder[elementsOrder.length - 1].styles[
        DISTRIBUTE_POSITION_LOOKUP[distribute]
      ]
        - (elementsOrder[0].styles[DISTRIBUTE_POSITION_LOOKUP[distribute]]
          + elementsOrder[0].styles[DISTRIBUTE_SIZE_LOOKUP[distribute]]);

      for (let i = 1; i < elementsOrder.length - 1; i += 1) {
        totalInnerElementsSize
          += elementsOrder[i].styles[DISTRIBUTE_SIZE_LOOKUP[distribute]];
      }

      const gapSpace = Math.floor(
        (innerSpace - totalInnerElementsSize) / (elementsOrder.length - 1),
      );

      for (let i = 0; i < elementsOrder.length - 2; i += 1) {
        const nextElement = find(
          selectedElements,
          (el) => el.id === elementsOrder[i + 1].id,
        );

        if (nextElement.group) {
          const group = this.getElementById(nextElement.group.id);
          const child = this.getElementById(nextElement.id);

          group.element.elements[child.index].styles[
            DISTRIBUTE_POSITION_LOOKUP[distribute]
          ] = elementsOrder[i].styles[DISTRIBUTE_POSITION_LOOKUP[distribute]]
            - nextElement.group.styles[DISTRIBUTE_POSITION_LOOKUP[distribute]]
            + (gapSpace
              + elementsOrder[i].styles[DISTRIBUTE_SIZE_LOOKUP[distribute]]);
        } else {
          const el = this.getElementById(nextElement.id);
          el.styles[DISTRIBUTE_POSITION_LOOKUP[distribute]] = elementsOrder[i].styles[DISTRIBUTE_SIZE_LOOKUP[distribute]]
            + elementsOrder[i].styles[DISTRIBUTE_POSITION_LOOKUP[distribute]]
            + gapSpace;
        }
      }

      EventBus.emit('sync-all');
    },
    getSelectedElements() {
      const selectedElements = [];

      this.selectedElements.forEach((id) => {
        let clone;
        const el = this.getElementById(id);

        if (!el) {
          return;
        }

        if (el.parent) {
          clone = cloneDeep(el);
          clone.styles.top = clone.styles.top + clone.parent.styles.top;
          clone.styles.left = clone.styles.left + clone.parent.styles.left;
          if (clone.parent && clone.parent.type === 'group') {
            clone.group = clone.parent;
          }
          selectedElements.push(clone);
        } else {
          selectedElements.push(el);
        }
      });

      return selectedElements;
    },

    /**
     * Fetch full rect that encompasses all selected elements.
     * Relative positioning of elements inside of groups is ignored,
     * and the absolute position is used instead
     */
    getSelectedElementPositions() {
      const elements = this.selectedElements.map((id) => this.getAbsolutePosition(id, this.getAllParents(id)));

      const positions = elements.reduce(
        (memo, element) => {
          const {
            top, left, height, width,
          } = element;

          const right = left + width;
          const bottom = top + height;

          return {
            top: Math.min(memo.top, top),
            right: Math.max(memo.right, right),
            bottom: Math.max(memo.bottom, bottom),
            left: Math.min(memo.left, left),
          };
        },
        {
          top: Infinity,
          right: -Infinity,
          bottom: -Infinity,
          left: Infinity,
        },
      );

      return {
        ...positions,
        height: positions.bottom - positions.top,
        width: positions.right - positions.left,
      };
    },

    registerCustomCSS() {
      const cssMarker = document.querySelector(
        "meta[name='custom-css-marker']",
      );

      if (this.getTemplateSnippets) {
        // Iterate over each snippet on this theme, and make sure the css gets added
        this.getTemplateSnippets.forEach((snpId) => {
          const snippetBlock = document.querySelector(`style#snp-${snpId}`);

          // We haven't yet added a style block for this snippet
          if (!snippetBlock) {
            // Find the snippet that matches this id and add the css block
            this.snippets.some((snippet) => {
              if (snippet._id !== snpId) {
                return false; // This isn't the right snippet, try the next one
              }

              const styleEl = document.createElement('style');
              styleEl.id = `snp-${snpId}`;
              styleEl.innerHTML = snippet.css;
              cssMarker.parentNode.insertBefore(styleEl, cssMarker);

              return true; // We found the snippet, we can stop iterating
            });
          }
        });
      }
    },
    beforeWheel(e) {
      this.scaleFactorMode = 1;
      const ctrlPressed = e.metaKey || e.ctrlKey;

      const shouldIgnore = !ctrlPressed;
      return shouldIgnore;
    },
    beforeMouseDown(e) {
      const isPressed = this.panMode || e.button === 1;

      const shouldIgnore = !isPressed;
      return shouldIgnore;
    },
    onZoomInit(panZoomInstance) {
      this.panZoomInstance = panZoomInstance;

      const transformData = panZoomInstance.getTransform();

      this.setScaleFactor(transformData.scale);
      this.scaleFactorSelected = transformData.scale * 100;

      this.resize();
    },
    onPanStart(e) {
      this.unbindDragSelector(e); // turn off other mouse drags
    },
    onZoom(e) {
      const transformData = e.getTransform();
      this.setScaleFactor(transformData.scale);

      this.resize();
      this.resizeTheCanvasMask();
    },
    onPan() {
      if (this.panMode) {
        this.$refs.panZoom.$el.style.cursor = 'grabbing';
      }

      this.trackRulerAndGuideScrollPosition();
      this.resizeTheCanvasMask();
    },

    selectElements() {
      const selectedElements = [];
      this.$refs.canvas.querySelectorAll('.element').forEach((el) => {
        const { classList } = el;

        // If the Alt key is held, ignore elements with class 'group'.
        // If the Alt key is not held, ignore elements with class 'group-item'.
        if (
          this.altKey
            ? classList.contains('group')
            : classList.contains('group-item')
        ) {
          return;
        }

        const unselectableClasses = [
          'locked',
          'slideshow-widget',
          'resizer',
          'social-group',
        ];

        // If the element has any common unselectable class, ignore it
        if (unselectableClasses.some((cls) => classList.contains(cls))) {
          return;
        }

        // Assuming intersects function can handle DOM elements directly
        if (intersects(this.dragSelector, el)) {
          const { id } = el;
          if (id && this.getElementById(id)) selectedElements.push(id);
        }
      });

      this.$store.dispatch('selectElements', selectedElements);
    },

    openSelector(e) {
      let w = Math.abs(this.initialW - e.pageX);
      let h = Math.abs(this.initialH - e.pageY);

      if (
        (e.shiftKey && this.queuedElement)
        || this.queuedElement?.value?.type === 'qrcode'
      ) {
        const xDist = e.clientX - this.elementDrawStartX;
        const yDist = e.clientY - this.elementDrawStartY;
        const minDist = Math.min(Math.abs(xDist), Math.abs(yDist));
        this.elementDrawEndX = this.elementDrawStartX + Math.sign(xDist) * minDist;
        this.elementDrawEndY = this.elementDrawStartY + Math.sign(yDist) * minDist;

        w = Math.abs(this.elementDrawEndX - this.elementDrawStartX);
        h = Math.abs(this.elementDrawEndY - this.elementDrawStartY);
      } else if (this.queuedElement) {
        this.elementDrawEndX = e.clientX;
        this.elementDrawEndY = e.clientY;
      }

      // Check for NaN and use 0 if true
      w = Number.isNaN(w) ? 0 : w;
      h = Number.isNaN(h) ? 0 : h;

      if (w > 10 || h > 10) {
        this.didDrag = true;
      }

      this.dragSelector.style.width = `${w}px`;
      this.dragSelector.style.height = `${h}px`;

      if (e.pageX <= this.initialW && e.pageY >= this.initialH) {
        this.dragSelector.style.left = `${e.pageX}px`;
      } else if (e.pageY <= this.initialH && e.pageX >= this.initialW) {
        this.dragSelector.style.top = `${e.pageY}px`;
      } else if (e.pageY < this.initialH && e.pageX < this.initialW) {
        this.dragSelector.style.left = `${e.pageX}px`;
        this.dragSelector.style.top = `${e.pageY}px`;
      }

      if (!this.queuedElement) {
        this.selectElements();
      }
    },

    unbindDragSelector() {
      document.removeEventListener('mousemove', this.openSelector);
      document.removeEventListener('mouseup', this.unbindDragSelector);

      this.dragSelector.classList.remove('active');
      this.dragSelector.style.width = '0px';
      this.dragSelector.style.height = '0px';
    },
  },
};
</script>

<style lang="scss" scoped>
.template-preview {
  flex: 1 1 auto;
  min-width: 0;
}

.drag-selector {
  .active {
    cursor: grab;
  }
}
</style>
